import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  useMemo,
  useCallback
} from 'react'
import { PaletteMode } from '@mui/material'

/**
 * ThemeModeContextType defines the shape of the theme mode context.
 */
interface ThemeModeContextType {
  /** The current palette mode ('light' or 'dark') */
  mode: PaletteMode
  /** Toggles between light and dark mode */
  toggleMode: () => void
}

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: 'dark',
  toggleMode: () => {}
})

/**
 * Safely reads the persisted theme mode from localStorage.
 * Falls back to 'dark' if storage is unavailable or value is invalid.
 */
const getStoredMode = (): PaletteMode => {
  try {
    const stored = localStorage.getItem('themeMode')
    return stored === 'light' || stored === 'dark' ? stored : 'dark'
  } catch {
    return 'dark'
  }
}

/**
 * Safely writes the theme mode to localStorage.
 */
const saveMode = (mode: PaletteMode) => {
  try {
    localStorage.setItem('themeMode', mode)
  } catch {
    // localStorage unavailable; ignore
  }
}

/**
 * ThemeModeProvider manages the application's light/dark mode state.
 * It persists the mode in localStorage and exposes a toggle function.
 */
export const ThemeModeProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const storedMode = getStoredMode()
    // Apply theme synchronously during initialization to prevent flash
    document.body.dataset.theme = storedMode
    return storedMode
  })

  // useLayoutEffect runs synchronously after DOM updates and before paint,
  // ensuring the data-theme attribute stays in sync when mode changes.
  useLayoutEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.dataset.theme = mode
    }
  }, [mode])

  const toggleMode = useCallback(() => {
    setMode(prev => {
      const next: PaletteMode = prev === 'light' ? 'dark' : 'light'
      saveMode(next)
      return next
    })
  }, [])

  const contextValue = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode])

  return (
    <ThemeModeContext.Provider value={contextValue}>
      {children}
    </ThemeModeContext.Provider>
  )
}

/**
 * useThemeMode hook provides access to the current theme mode and toggle function.
 * @returns {ThemeModeContextType} The theme mode context value
 */
export const useThemeMode = () => useContext(ThemeModeContext)
